import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useState } from 'react';
import { JURISDICTION_LIST } from '@/lib/constants';
import {
  apiErrorText,
  jurisdictionTitle,
  PARTY_ROLE_LABELS,
  type PartyLink,
  type PartyRole,
  useLinkParty,
  usePartyCandidates,
} from '@/models/clientCompanies';

type Kind = 'person' | 'company';
type Mode = 'existing' | 'new';

const JURISDICTION_OPTIONS = JURISDICTION_LIST.map(({ code, title }) => ({
  value: code,
  label: `${title} (${code})`,
}));

export function AddPartyModal({
  companyId,
  role,
  onClose,
}: {
  companyId: number;
  role: PartyRole;
  onClose: () => void;
}) {
  const allowCompany = role === 'shareholder';
  const [kind, setKind] = useState<Kind>('person');
  const [mode, setMode] = useState<Mode>('existing');
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search.trim(), 300);
  const [selected, setSelected] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ value: string; label: string } | null>(
    null,
  );
  const [person, setPerson] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [external, setExternal] = useState({
    name: '',
    jurisdiction: null as string | null,
    registrationNumber: '',
  });
  const [share, setShare] = useState<number | string>('');
  const [error, setError] = useState<string | null>(null);
  const q = picked && search === picked.label ? '' : debounced;
  const link = useLinkParty();
  const candidates = usePartyCandidates(companyId, q, mode === 'existing');

  const contactOptions = (candidates.data?.contacts ?? []).map((c) => {
    const name =
      [c.firstName, c.lastName].filter(Boolean).join(' ') || `#${c.id}`;
    return {
      value: String(c.id),
      label: `${name}${c.email ? ` - ${c.email}` : ''}${c.sameAccount ? '' : ` (${c.accountName ?? 'other account'})`}`,
    };
  });
  const companyOptions = (candidates.data?.companies ?? []).map((c) => ({
    value: String(c.id),
    label: `${c.name}${c.registrationNumber ? ` - ${c.registrationNumber}` : ''}${c.jurisdiction ? `, ${jurisdictionTitle(c.jurisdiction)}` : ''}${c.kind === 'external' ? ' (external)' : ''}`,
  }));

  const baseOptions = kind === 'person' ? contactOptions : companyOptions;
  const options =
    picked && !baseOptions.some((o) => o.value === picked.value)
      ? [picked, ...baseOptions]
      : baseOptions;

  const reset = (next: Partial<{ kind: Kind; mode: Mode }>) => {
    if (next.kind) setKind(next.kind);
    if (next.mode) setMode(next.mode);
    setSelected(null);
    setPicked(null);
    setSearch('');
    setError(null);
  };

  const canSubmit =
    mode === 'existing'
      ? !!selected
      : kind === 'person'
        ? !!(person.firstName.trim() || person.lastName.trim())
        : !!external.name.trim();

  const submit = async () => {
    const body: PartyLink = { companyId, role };
    if (mode === 'existing' && kind === 'person')
      body.contactId = Number(selected);
    if (mode === 'existing' && kind === 'company')
      body.partyCompanyId = Number(selected);
    if (mode === 'new' && kind === 'person')
      body.newContact = {
        firstName: person.firstName.trim() || undefined,
        lastName: person.lastName.trim() || undefined,
        email: person.email.trim() || undefined,
        phone: person.phone.trim() || undefined,
      };
    if (mode === 'new' && kind === 'company')
      body.newExternal = {
        name: external.name.trim(),
        jurisdiction: external.jurisdiction ?? undefined,
        registrationNumber: external.registrationNumber.trim() || undefined,
      };
    if (role === 'shareholder' && share !== '')
      body.sharePercent = Number(share);
    setError(null);
    try {
      await link.mutateAsync(body);
      onClose();
    } catch (e) {
      const text = apiErrorText(e);
      setError(
        text.includes('PARTY_EXISTS')
          ? `Already linked as ${PARTY_ROLE_LABELS[role].toLowerCase()}`
          : text,
      );
    }
  };

  return (
    <Modal opened onClose={onClose} title={`Add ${PARTY_ROLE_LABELS[role]}`}>
      <Stack gap="sm">
        {allowCompany ? (
          <SegmentedControl
            size="xs"
            value={kind}
            onChange={(v) => reset({ kind: v as Kind })}
            data={[
              { value: 'person', label: 'Person' },
              { value: 'company', label: 'Company' },
            ]}
          />
        ) : (
          <Text fz="xs" c="dimmed">
            {PARTY_ROLE_LABELS[role]} is always a person (a BO contact).
          </Text>
        )}
        <SegmentedControl
          size="xs"
          value={mode}
          onChange={(v) => reset({ mode: v as Mode })}
          data={[
            {
              value: 'existing',
              label:
                kind === 'person' ? 'Existing contact' : 'Existing company',
            },
            {
              value: 'new',
              label: kind === 'person' ? 'New contact' : 'New external company',
            },
          ]}
        />

        {mode === 'existing' && (
          <Select
            label={kind === 'person' ? 'Contact' : 'Company'}
            placeholder={
              kind === 'person'
                ? 'Search by name or email'
                : 'Type company name or reg. number'
            }
            searchable
            data={options}
            value={selected}
            onChange={(v, option) => {
              setSelected(v);
              setPicked(v ? { value: v, label: option.label } : null);
            }}
            searchValue={search}
            onSearchChange={setSearch}
            filter={({ options }) => options}
            nothingFoundMessage={
              kind === 'company' && !q ? 'Start typing' : 'Nothing found'
            }
            description={
              kind === 'person'
                ? 'Contacts of the company account are listed first'
                : 'Client companies and external companies'
            }
          />
        )}

        {mode === 'new' && kind === 'person' && (
          <>
            <Group grow gap="xs">
              <TextInput
                label="First name"
                value={person.firstName}
                onChange={(e) =>
                  setPerson({ ...person, firstName: e.currentTarget.value })
                }
              />
              <TextInput
                label="Last name"
                value={person.lastName}
                onChange={(e) =>
                  setPerson({ ...person, lastName: e.currentTarget.value })
                }
              />
            </Group>
            <Group grow gap="xs">
              <TextInput
                label="Email"
                value={person.email}
                onChange={(e) =>
                  setPerson({ ...person, email: e.currentTarget.value })
                }
              />
              <TextInput
                label="Phone"
                value={person.phone}
                onChange={(e) =>
                  setPerson({ ...person, phone: e.currentTarget.value })
                }
              />
            </Group>
            <Text fz="xs" c="dimmed">
              The contact is created in the company account. Email and phone are
              optional.
            </Text>
          </>
        )}

        {mode === 'new' && kind === 'company' && (
          <>
            <TextInput
              label="Company name"
              withAsterisk
              value={external.name}
              onChange={(e) =>
                setExternal({ ...external, name: e.currentTarget.value })
              }
            />
            <Group grow gap="xs">
              <Select
                label="Jurisdiction"
                data={JURISDICTION_OPTIONS}
                searchable
                clearable
                value={external.jurisdiction}
                onChange={(v) => setExternal({ ...external, jurisdiction: v })}
              />
              <TextInput
                label="Registration number"
                value={external.registrationNumber}
                onChange={(e) =>
                  setExternal({
                    ...external,
                    registrationNumber: e.currentTarget.value,
                  })
                }
              />
            </Group>
            <Text fz="xs" c="dimmed">
              An external company is a record outside the client portfolio.
            </Text>
          </>
        )}

        {role === 'shareholder' && (
          <NumberInput
            label="Share, %"
            placeholder="Optional"
            min={0.01}
            max={100}
            decimalScale={2}
            value={share}
            onChange={setShare}
          />
        )}

        {error && (
          <Alert color="red" variant="light" p="xs">
            <Text fz="xs">{error}</Text>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            loading={link.isPending}
          >
            Add
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
